import i18n from 'helpers/i18n';
import messages from './messages';
import { useEffect } from 'react';
import { useMeasure } from 'context/measure';
import FooterNav from 'components/Measure/View/FooterNav';

import {
  FeedbackSection,
  CustomQuestionsSection
} from 'components/Measure/View/ExamplesAndNotes/CustomQuestionsSection';

import { useObservationSettings } from 'actions/envScales/observationSettings';
import { useObservationSuggestions } from 'actions/envScales/observations';
import Attachments from 'components/Measure/View/ExamplesAndNotes/Attachments/Attachments';
import ClassroomExamplesSection from 'components/Measure/View/ExamplesAndNotes/ClassroomExamplesSection';
import { useValidations } from 'components/Measure/View/ValidationsContext';
import { toastError } from 'components/Toast';

function ExamplesAndNotes() {
  const { assessment, refresh } = useMeasure();
  const observationId = assessment.observation!.id;
  const { validate } = useValidations();

  const { data: settings } = useObservationSettings({
    assessmentId: assessment.id
  });

  // Warms the suggestions cache in parallel with the settings request.
  // ClassroomExamplesSection consumes the same query, and without this it could
  // not start fetching until settings resolved and it mounted, serializing two
  // round trips.
  useObservationSuggestions(observationId);

  /**
   * Cycle scores/notes are entered on sibling routes sharing this provider,
   * which loads once. Revalidate so the Observation Feedback scores table and
   * notes aren't showing the assessment as first opened. `refresh()` resolves
   * behind the current screen, so this doesn't block the page.
   */
  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!settings) {
    return (
      <div className="py-16 text-center">
        <i
          aria-label="loading"
          role="status"
          className="fa-solid fa-spinner animate-spin text-xl"
        />
      </div>
    );
  }

  const {
    use_custom_questions: displayCustomQuestions,
    strength_and_growth_enabled: displayClassroomExamples,
    dimension_feedback: displayDimension,
    domain_feedback: displayDomain,
    require_attachments: requireAttachments
  } = settings;

  function getIsFirstSection() {
    const sections = [
      displayCustomQuestions ? 'questions' : null,
      displayDimension ? 'dimensions' : null,
      displayDomain ? 'domains' : null,
      displayClassroomExamples ? 'classrooms' : null,
      'attachments'
    ].filter(s => s !== null);

    const firstSection = sections[0];

    return {
      questions: firstSection === 'questions',
      dimensions: firstSection === 'dimensions',
      domains: firstSection === 'domains',
      classrooms: firstSection === 'classrooms',
      attachments: firstSection === 'attachments'
    };
  }

  async function handleValidateNext() {
    const shouldTransitionToSummary = await validate();

    if (!shouldTransitionToSummary) {
      toastError({
        description: i18n.ft(messages.summaryError)
      });
    }

    return shouldTransitionToSummary;
  }

  const isFirstSection = getIsFirstSection();

  return (
    <>
      <div className="flex flex-col gap-2">
        {displayCustomQuestions && (
          <CustomQuestionsSection assessmentId={assessment.id} attached />
        )}

        {displayDimension && (
          <FeedbackSection
            observationId={observationId}
            targetType="dimensions"
            attached={isFirstSection.dimensions}
          />
        )}

        {displayDomain && (
          <FeedbackSection
            observationId={observationId}
            targetType="domains"
            attached={isFirstSection.domains}
          />
        )}

        {displayClassroomExamples && (
          <ClassroomExamplesSection attached={isFirstSection.classrooms} />
        )}

        <Attachments
          assessment={assessment}
          sectionWithRoundedBorder={isFirstSection.attachments}
          requireAttachments={requireAttachments}
        />
      </div>

      <FooterNav validateNext={handleValidateNext} />
    </>
  );
}

export default ExamplesAndNotes;
